import { Button } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { type PlotParams } from "@nens/plotly";

import Plot, { createTimeHoverProps } from "../components/ThreediPlot";
import DownloadCsvIcon from "../icons/download_csv_icon.svg?react";
import { WaterLevelChart } from "../state/selectedAsset";
import { getSimulation } from "../state/simulation";
import { Asset } from "../types/assets";
import { forceValidOrNull, isValidData } from "../utils/isValidData";

import { PropertyTableHeader } from "./PropertyTableHeader";

interface Props {
  asset: Asset;
  data: WaterLevelChart;
  type: ChartType;
}

export type ChartType =
  | "Waterlevel"
  | "WaterDepth"
  | "GroundwaterLevel"
  | "Velocity"
  | "Discharge"
  | "BreachWidth"
  | "BreachDepth"
  | "BreachArea"
  | "RainfallOnPointChart";

// Helper function get chart's parameter, unit and name based on chart type
const getChartParameters = (
  chartType: ChartType
): {
  name: string;
  unit: string;
  parameter: string;
} => {
  if (chartType === "Waterlevel") {
    return {
      name: "Water level",
      unit: "m",
      parameter: "water_level",
    };
  } else if (chartType === "WaterDepth") {
    return {
      name: "Water depth",
      unit: "m",
      parameter: "water_depth",
    };
  } else if (chartType === "GroundwaterLevel") {
    return {
      name: "Groundwater level",
      unit: "m",
      parameter: "groundwater_level",
    };
  } else if (chartType === "Velocity") {
    return {
      name: "Velocity",
      unit: "m/s",
      parameter: "velocity",
    };
  } else if (chartType === "Discharge") {
    return {
      name: "Discharge",
      unit: "m³/s",
      parameter: "discharge",
    };
  } else if (chartType === "BreachWidth") {
    return {
      name: "Width",
      unit: "m",
      parameter: "breach_width",
    };
  } else if (chartType === "BreachDepth") {
    return {
      name: "Depth",
      unit: "m",
      parameter: "breach_depth",
    };
  } else if (chartType === "RainfallOnPointChart") {
    return {
      name: "Rainfall (mm/h)",
      unit: "mm/h",
      parameter: "rainfall_mpers",
    };
  } else {
    //chartType === "BreachArea"
    return {
      name: "Wet cross-sectional area (m²)",
      unit: "m²",
      parameter: "area",
    };
  }
};

// Helper function to export timeseries data to CSV file
export const exportToCSV = (
  chartType: ChartType,
  timestamps: number[],
  valueArray: (number | null)[],
  filename: string
) => {
  // Set parameter and unit based on dataType
  const { name, unit } = getChartParameters(chartType);
  // Create CSV file
  const timeseries = timestamps.map((time, i) => [time, valueArray[i]]);
  const csvHeader =
    `Timeseries downloaded from Rana Lava session.\n` +
    `Attention: The series may be sampled. For details use the resulting (aggregated) netCDF instead.\n\n` +
    `Time in simulation (s),${name} (${unit})\n`;
  const csvContent = csvHeader + timeseries.map((dataArray) => dataArray.join(",")).join("\n");
  const csvFile = new Blob([csvContent], { type: "text/csv" });
  const csvURL = URL.createObjectURL(csvFile);

  // Download CSV with a generated filename
  const downloadLink = document.createElement("a");
  downloadLink.href = csvURL;
  downloadLink.download = filename;
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
};

// Helper function to generate filename
export const getFilename = (simulationName: string, siteId: string, chartType: ChartType) => {
  const chartParameter = getChartParameters(chartType).parameter;
  return `${simulationName}_${siteId}_${chartParameter}.csv`;
};

const TimeseriesChart: React.FC<Props> = (props) => {
  const { data, type, asset } = props;
  const waterTimeseriesGraph = data;
  const minimum = Math.min(
    ...(waterTimeseriesGraph || []).map((item) => item.value).filter((numb) => isValidData(numb))
  );
  // even if no groundwater value is there minimum still need to be set for that timestep otherwise plotly tonexty behaves wrong.
  const minimumData = (waterTimeseriesGraph || []).map((_item) => minimum - 0.1); // in case groundwater is constant we still need to show an area/fill. Therefore - 0.1

  const simulationName = useSelector(getSimulation).name;

  const waterHeight = forceValidOrNull(
    (waterTimeseriesGraph || []).map(({ value }) => {
      if (type === "RainfallOnPointChart" && "sumax" in asset.properties)
        return (value * 60 * 60 * 1000) / asset.properties.sumax;
      return value;
    })
  );
  const timestamps = (waterTimeseriesGraph || []).map((element) => element.timestamp);
  const timeOfWaterHeight = (waterTimeseriesGraph || []).map((element) => {
    const originalDate = new Date("2020-01-01T00:00:00Z");
    originalDate.setSeconds(originalDate.getSeconds() + element.timestamp);
    return originalDate.toISOString();
  });
  const lastTimeWaterTimeseriesGraph = (function () {
    const originalDate = new Date("2020-01-01T00:00:00Z");
    if (waterTimeseriesGraph.length && waterTimeseriesGraph[waterTimeseriesGraph.length - 1]) {
      const lastValue = waterTimeseriesGraph[waterTimeseriesGraph.length - 1];
      originalDate.setSeconds(originalDate.getSeconds() + lastValue.timestamp + 900);
    }
    return originalDate.toISOString();
  })();

  const intl = useIntl();
  const eventualChartData: PlotParams["data"] = [
    {
      ...createTimeHoverProps(timeOfWaterHeight),
      y: waterHeight,
      type: "scatter",
      mode: "lines+markers",
      marker: { color: "#699fbe" },
      fill:
        type === "Waterlevel" || type === "RainfallOnPointChart" || type === "Velocity"
          ? "none"
          : type === "WaterDepth" ||
            type === "BreachWidth" ||
            type === "BreachDepth" ||
            type === "BreachArea"
          ? "tozeroy"
          : "tonexty",
    },
  ];
  // If it concerns groundwater then the area of the chart need to be filled, but this area does not go to zero, because groundwater is often below zero.
  // Therefore this additional constant line need to be added in front of the real data to make the "tonexty" feature of plotly work.
  if (type === "GroundwaterLevel") {
    eventualChartData.unshift({
      ...createTimeHoverProps(timeOfWaterHeight),
      y: minimumData,
      type: "scatter",
      mode: "lines",
      showlegend: false,
      hoverinfo: "skip",
      line: { width: 0 }, // placeholder needs to be invisible so width 0
    });
  }

  return (
    <div>
      <PropertyTableHeader
        button={
          <Button
            variant="secondary"
            leftIcon={<DownloadCsvIcon />}
            onClick={() =>
              exportToCSV(
                type,
                timestamps,
                waterHeight,
                getFilename(simulationName, props.asset.properties.site_id, type)
              )
            }
          >
            CSV
          </Button>
        }
      >
        {type === "Waterlevel" ? (
          <FormattedMessage
            id="timeseriesWaterLevelChart.chartTitleWaterHeight"
            defaultMessage="Water level (mMSL)"
          />
        ) : type === "WaterDepth" ? (
          <FormattedMessage
            id="timeseriesWaterLevelChart.chartTitleWaterDepth"
            defaultMessage="Water depth (m)"
          />
        ) : type === "RainfallOnPointChart" ? (
          <FormattedMessage
            id="timeseriesChart.chartTitleRainfall"
            defaultMessage="Rainfall (mm/h)"
          />
        ) : type === "BreachWidth" ? (
          <FormattedMessage
            id="timeseriesWaterLevelChart.chartTitleBreachWidth"
            defaultMessage="Width (m)"
          />
        ) : type === "BreachDepth" ? (
          <FormattedMessage
            id="timeseriesWaterLevelChart.chartTitleBreachDepth"
            defaultMessage="Depth (m)"
          />
        ) : type === "BreachArea" ? (
          <FormattedMessage
            id="timeseriesWaterLevelChart.chartTitleBreachArea"
            defaultMessage="Wet cross-sectional area (m²)"
          />
        ) : type === "Velocity" ? (
          <FormattedMessage
            id="timeseriesWaterFlowChart.chartTitleVelocity"
            defaultMessage="Velocity (m/s)"
          />
        ) : (
          <FormattedMessage
            id="timeseriesWaterLevelChart.chartTitleGroundwaterLevel"
            defaultMessage="Groundwater level (mMSL)"
          />
        )}
      </PropertyTableHeader>
      <Plot
        style={{
          position: "relative",
          display: "inline-block",
          border: "1px solid #707070",
          borderRadius: 5,
          overflow: "hidden",
        }}
        data={eventualChartData}
        layout={{
          showlegend: false,
          width: 335,
          height: 335,
          paper_bgcolor: "#f2f2ec",
          plot_bgcolor: "#f2f2ec",
          margin: {
            l: 40,
            r: 10,
            b: 10,
            t: 10,
          },
          xaxis: {
            tickformat:
              "%H:%M\n" +
              intl.formatMessage({
                id: "timeseriesWaterLevelChart.chartXAxisLabel",
                defaultMessage: "day",
              }) +
              "%_d",
            // solution from // https://github.com/plotly/plotly.js/issues/1464 that didnot work
            // tickformat: "bla###%H:%M",
            title: {
              text: intl.formatMessage({
                id: "timeseriesWaterLevelChart.chartXAxisTime",
                defaultMessage: "Time (hours : mins) &#8594;",
              }),
              standoff: 10,
            },
            showticklabels: timeOfWaterHeight.length > 0,
            zeroline: false,
            automargin: true,
            range: [timeOfWaterHeight[0], lastTimeWaterTimeseriesGraph],
          },
          yaxis: {
            title: {
              text:
                type === "WaterDepth" || type === "BreachWidth" || type === "BreachDepth"
                  ? "(m) &#8594;"
                  : type === "BreachArea"
                  ? "(m²) &#8594;"
                  : type === "RainfallOnPointChart"
                  ? "(mm/h) &#8594;"
                  : type === "Velocity"
                  ? "(m/s) &#8594;"
                  : "(mMSL) &#8594;",
              standoff: 5,
            },
            exponentformat: "power",
            showline: true,
            zeroline:
              type === "WaterDepth" ||
              type === "BreachWidth" ||
              type === "BreachDepth" ||
              type === "BreachArea" ||
              type === "RainfallOnPointChart"
                ? true
                : false,
            automargin: true,
            rangemode:
              type === "WaterDepth" ||
              type === "BreachWidth" ||
              type === "BreachDepth" ||
              type === "BreachArea" ||
              type === "RainfallOnPointChart"
                ? "nonnegative"
                : "normal",
          },
        }}
      />
    </div>
  );
};

export default TimeseriesChart;
